/**
 * The agent's question, as data — routing, adapters, and the controlled answer map.
 *
 * `requestClarification` sends **one to five questions in a single call**, each `open` | `single` |
 * `multiple` with labelled options. That one event has to become two experiences, and the split is
 * decided here rather than in a component so the transcript and the composer can never disagree
 * about which one is happening.
 *
 * ## Why the answers are a `Record<QuestionId, string[]>`
 *
 * The shipping `Question.tsx` holds two `Map`s in internal state — `selectedOptions` keyed by
 * question to option *ids*, and `textInputs` keyed by question to a free string — and hydrates both
 * from `initial*` props. API-PARITY §1.7 is blunt about why that has to go: a `Map` prop is not
 * serialisable, breaks referential equality on every render, and leaves the card uncontrolled, so
 * the parent cannot re-render it from server state.
 *
 * One map of **labels** replaces both, and the collapse is not lossy: the value a question resolves
 * to is a list of labels either way (`handleFinalSubmit` in the old card looks every option id back
 * up to its label before submitting), and the free-text entry is simply the one value in the list
 * that is not an option label. `splitValues` below is that read, and it is the only place the two
 * halves are told apart.
 *
 * The one shape that cannot survive the round trip is a user typing free text **identical** to an
 * option they were offered. It comes back as that option selected with an empty text box. Nothing
 * downstream can tell the difference either — the wire carries labels, not ids — so this is a
 * property of the protocol rather than of the collapse.
 */
import type { ClarificationAnswer, ClarificationItem } from '~/types/planning';

/* ── the types, kept verbatim from `PlanningChat/ui/Question` (API-PARITY §1.7) ──────────────── */

export type QuestionId = string;
export type OptionId = string;
export type QuestionType = 'single' | 'multiple' | 'open';

export interface QuestionOption {
  id: string;
  label: string;
}

export interface QuestionData {
  id: string;
  question: string;
  type: QuestionType;
  options?: QuestionOption[];
}

export interface QuestionAnswer {
  questionId: QuestionId;
  question: string;
  value: string[] | null;
  skipped: boolean;
}

/**
 * The controlled state, in place of the old card's two `Map`s.
 *
 * Values are **labels**, in the order the user picked them. An empty or absent entry is an
 * unanswered question, which is the same thing as a skipped one at submit time.
 */
export type QuestionAnswers = Record<QuestionId, string[]>;

/* ── the routing rule ───────────────────────────────────────────────────────────────────────── */

/**
 * Which of the two experiences one request gets.
 *
 * `inline` is the cheap one and it is deliberately narrow: **one** question, `single`, at most four
 * options. That is exactly the shape v1.5 already draws — `AgentTurn`'s *Asking back* board, an
 * `ActivityBlock` whose body is a sentence over a wrapping row of chips — so it costs the reader a
 * tap and never takes the composer away.
 *
 * Everything else is the wizard: more than one question needs a stepper and a summary, `open` needs
 * a real text box, `multiple` needs commit-then-send rather than tap-to-answer, and five chips wrap
 * to a third line in a 380px pane. `none` is the degenerate request with nothing in it — it can
 * arrive from a malformed tool call, and it must not blank the composer.
 */
export type QuestionRoute = 'inline' | 'wizard' | 'none';

/** At most this many options still read as a row of chips rather than a list. */
const INLINE_OPTION_CEILING = 4;

export function routeOf(items: ClarificationItem[] | undefined): QuestionRoute {
  const list = items ?? [];
  if (list.length === 0) return 'none';
  if (list.length > 1) return 'wizard';
  const only = list[0];
  if (only.type !== 'single') return 'wizard';
  const options = only.options ?? [];
  if (options.length === 0 || options.length > INLINE_OPTION_CEILING) return 'wizard';
  return 'inline';
}

/* ── the wire, adapted ──────────────────────────────────────────────────────────────────────── */

/**
 * `ClarificationItem[]` -> the card's questions.
 *
 * The ids are `clarification-${index}` / `opt-${index}`, which is not an arbitrary choice: it is the
 * spelling `ClarificationRequestCard` already uses, so a request that was half-answered before a
 * reload seeds against the same keys.
 */
export function toQuestions(items: ClarificationItem[]): QuestionData[] {
  return items.map((item, index) => ({
    id: `clarification-${index}`,
    question: item.question,
    // The backend leaves `type` off for a plain ask, and an ask with no type is an open one.
    type: (item.type ?? 'open') as QuestionType,
    options: item.options?.map((option, optionIndex) => ({
      id: `opt-${optionIndex}`,
      label: option.label,
    })),
  }));
}

/**
 * An answer string that came back off the wire, split into the values that produced it.
 *
 * `toClarificationAnswers` joins with `', '`, so the split is the inverse — but only when every
 * piece is an option the user was actually offered. A free-text answer that happens to contain a
 * comma would otherwise be torn into two answers, which is worse than not splitting at all.
 */
function valuesFromAnswer(item: ClarificationItem, raw: string): string[] {
  const labels = new Set((item.options ?? []).map((option) => option.label));
  const pieces = raw
    .split(', ')
    .map((piece) => piece.trim())
    .filter(Boolean);
  if (pieces.length > 1 && pieces.every((piece) => labels.has(piece))) return pieces;
  const single = raw.trim();
  return single ? [single] : [];
}

/** Answers already on the request — a card resumed after a reload opens where it was left. */
export function seedAnswers(items: ClarificationItem[]): QuestionAnswers {
  const seeded: QuestionAnswers = {};
  items.forEach((item, index) => {
    if (item.skipped || !item.answer) return;
    const values = valuesFromAnswer(item, item.answer);
    if (values.length) seeded[`clarification-${index}`] = values;
  });
  return seeded;
}

/** Which screen a resumed card opens on: the last question that already has something on it. */
export function seedIndex(items: ClarificationItem[]): number {
  let last = 0;
  items.forEach((item, index) => {
    if (!item.skipped && item.answer) last = index;
  });
  return last;
}

/* ── reading one question's values ──────────────────────────────────────────────────────────── */

export interface SplitValues {
  /** Option ids the values matched, in the question's own order of offer. */
  optionIds: OptionId[];
  /** Everything that matched no option — the "Something else" box's contents. */
  text: string;
}

/** The one place a flat value list is told apart into picked options and typed text. */
export function splitValues(question: QuestionData, values: string[] | undefined): SplitValues {
  const byLabel = new Map((question.options ?? []).map((option) => [option.label, option.id]));
  const optionIds: OptionId[] = [];
  const loose: string[] = [];
  (values ?? []).forEach((value) => {
    const id = byLabel.get(value);
    if (id) optionIds.push(id);
    else loose.push(value);
  });
  // More than one loose value cannot be produced by this card — it is defensive against a seeded
  // answer whose pieces half-matched — and joining beats silently dropping the tail.
  return { optionIds, text: loose.join(' ') };
}

/* ── writing one question's values ──────────────────────────────────────────────────────────── */

function labelOf(question: QuestionData, optionId: OptionId): string | undefined {
  return question.options?.find((option) => option.id === optionId)?.label;
}

/**
 * Tapping an option.
 *
 * `single` replaces everything, free text included — one answer means one answer. `multiple`
 * toggles the label in and out and leaves the typed text alone.
 */
export function pickOption(
  question: QuestionData,
  values: string[] | undefined,
  optionId: OptionId,
): string[] {
  const label = labelOf(question, optionId);
  if (!label) return values ?? [];
  if (question.type === 'single') return [label];
  const current = values ?? [];
  return current.includes(label) ? current.filter((value) => value !== label) : [...current, label];
}

/**
 * Typing in the free-text box.
 *
 * On `single` the text IS the answer, so anything picked is cleared — the old card did the same,
 * one event earlier, by wiping the selection on focus. Elsewhere the text is one more value beside
 * the options, and an emptied box removes it rather than leaving a blank entry behind.
 */
export function setFreeText(
  question: QuestionData,
  values: string[] | undefined,
  text: string,
): string[] {
  if (question.type === 'single') return text ? [text] : [];
  const { optionIds } = splitValues(question, values);
  const labels = optionIds
    .map((id) => labelOf(question, id))
    .filter((label): label is string => Boolean(label));
  return text ? [...labels, text] : labels;
}

/** Clicking into the free-text box on a `single` question drops the radio, exactly as before. */
export function clearForFreeText(question: QuestionData, values: string[] | undefined): string[] {
  if (question.type !== 'single') return values ?? [];
  const { text } = splitValues(question, values);
  return text ? [text] : [];
}

/* ── submitting ─────────────────────────────────────────────────────────────────────────────── */

/** Has this question got anything on it at all? Drives Next's disabled state and the summary. */
export function isAnswered(values: string[] | undefined): boolean {
  return (values ?? []).some((value) => value.trim().length > 0);
}

/** How many individual choices are on the card. The `N selected` run. */
export function selectedCount(answers: QuestionAnswers): number {
  return Object.values(answers).reduce(
    (total, values) => total + values.filter((value) => value.trim().length > 0).length,
    0,
  );
}

/** What one question reads as on the summary screen — `Existing investors and the exec team`. */
export function answerText(values: string[] | undefined): string {
  return (values ?? []).map((value) => value.trim()).filter(Boolean).join(' and ');
}

/** The card's answers, in the shape `Question.tsx` produced them. */
export function toQuestionAnswers(
  questions: QuestionData[],
  answers: QuestionAnswers,
): QuestionAnswer[] {
  return questions.map((question) => {
    const clean = (answers[question.id] ?? []).map((value) => value.trim()).filter(Boolean);
    // A `single` question can only ever have carried one value, but a seeded answer could arrive
    // with two; the card must not send both under a question that offered a radio.
    const value = question.type === 'single' ? clean.slice(0, 1) : clean;
    return {
      questionId: question.id,
      question: question.question,
      value: value.length > 0 ? value : null,
      skipped: value.length === 0,
    };
  });
}

/** The card's answers, on the wire. `', '` is the separator `ClarificationRequestCard` uses. */
export function toClarificationAnswers(answers: QuestionAnswer[]): ClarificationAnswer[] {
  return answers.map((answer) => ({
    question: answer.question,
    answer: answer.value ? answer.value.join(', ') : null,
    skipped: answer.skipped,
  }));
}

/** Every question refused at once — the x and the Skip on the last screen. */
export function skipEverything(items: ClarificationItem[]): ClarificationAnswer[] {
  return items.map((item) => ({ question: item.question, answer: null, skipped: true }));
}

/** One inline chip, answered on the tap. The other four questions do not exist on this route. */
export function answerInline(item: ClarificationItem, label: string): ClarificationAnswer[] {
  return [{ question: item.question, answer: label, skipped: false }];
}

/* ── the answered summary ───────────────────────────────────────────────────────────────────── */

export interface AnsweredRow {
  question: string;
  /** The answer, or the word the design uses for a question that was passed over. */
  answer: string;
  skipped: boolean;
}

/**
 * A `COMPLETED` request, as rows.
 *
 * `skipped` is derived rather than trusted: the backend sets the flag, but a request that came back
 * with the flag clear and the answer null is the same thing to a reader, and both shapes exist in
 * the database.
 */
export function answeredRows(items: ClarificationItem[]): AnsweredRow[] {
  return items.map((item) => {
    const skipped = item.skipped || item.answer == null || item.answer === '';
    return { question: item.question, answer: skipped ? 'Skipped' : (item.answer as string), skipped };
  });
}

/**
 * The header line over a question, on both routes and in both states.
 *
 * `request.message` is the agent's own framing when it sends one; the fallbacks are the strings the
 * design draws — *Asking back*'s row reads "One thing first" — pluralised for a stepper.
 */
export function questionHeading(count: number, message?: string): string {
  if (message && message.trim()) return message.trim();
  return count > 1 ? 'A few things first' : 'One thing first';
}
